/**
 * Meetings Hooks
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchMeetings,
  fetchMeeting,
  fetchMeetingTranscript,
  createMeeting,
  updateMeeting,
  deleteMeeting,
  startMeeting,
  finishMeeting,
  cancelMeeting,
  fetchCalendarEvents,
} from '../api';
import type {
  MeetingsQueryParams,
  CreateMeetingInput,
  UpdateMeetingInput,
} from '../types';

// =============================================================================
// Query Key Factory
// =============================================================================

export const meetingsKeys = {
  all: ['meetings'] as const,
  lists: () => [...meetingsKeys.all, 'list'] as const,
  list: (params: MeetingsQueryParams) => [...meetingsKeys.lists(), params] as const,
  details: () => [...meetingsKeys.all, 'detail'] as const,
  detail: (id: string) => [...meetingsKeys.details(), id] as const,
  transcripts: () => [...meetingsKeys.all, 'transcript'] as const,
  transcript: (id: string) => [...meetingsKeys.transcripts(), id] as const,
};

// =============================================================================
// Query Hooks
// =============================================================================

/**
 * Strips empty-string / undefined / null filter params before they enter the
 * query key and the request, so clearing a filter reuses the unfiltered cache
 * entry instead of creating a new phantom key that hits the network.
 */
function compactParams(params: MeetingsQueryParams): MeetingsQueryParams {
  const out: MeetingsQueryParams = {};
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') {
      (out as Record<string, unknown>)[k] = v;
    }
  }
  return out;
}

export function useMeetings(params: MeetingsQueryParams = {}) {
  const compact = compactParams(params);
  return useQuery({
    queryKey: meetingsKeys.list(compact),
    queryFn: () => fetchMeetings(compact),
  });
}

export function useCalendarEvents(params: { days?: number; calendarId?: string } = {}) {
  return useQuery({
    queryKey: [...meetingsKeys.all, 'calendar-events', params] as const,
    queryFn: () => fetchCalendarEvents(params),
    staleTime: 5 * 60 * 1000, // 5 min — calendar doesn't change that often
  });
}

export function useMeeting(id: string | undefined) {
  return useQuery({
    queryKey: meetingsKeys.detail(id!),
    queryFn: () => fetchMeeting(id!),
    enabled: !!id,
  });
}

/**
 * Lazy transcript fetch — only fires when `enabled` is true (caller opens the viewer).
 * Transcripts don't change often; stale for 10 min.
 */
export function useMeetingTranscript(id: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: meetingsKeys.transcript(id!),
    queryFn: () => fetchMeetingTranscript(id!),
    enabled: !!id && enabled,
    staleTime: 10 * 60 * 1000,
  });
}

// =============================================================================
// Mutation Hooks
// =============================================================================

export function useCreateMeeting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateMeetingInput) => createMeeting(input),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: meetingsKeys.lists() });
    },
  });
}

export function useUpdateMeeting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateMeetingInput }) =>
      updateMeeting(id, input),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: meetingsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: meetingsKeys.detail(data.id) });
    },
  });
}

export function useDeleteMeeting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => deleteMeeting(id),
    onSuccess: (_data, id) => {
      queryClient.invalidateQueries({ queryKey: meetingsKeys.lists() });
      queryClient.removeQueries({ queryKey: meetingsKeys.detail(id) });
    },
  });
}

export function useStartMeeting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => startMeeting(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: meetingsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: meetingsKeys.detail(data.id) });
    },
  });
}

export function useFinishMeeting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => finishMeeting(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: meetingsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: meetingsKeys.detail(data.id) });
    },
  });
}

export function useCancelMeeting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => cancelMeeting(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: meetingsKeys.lists() });
      queryClient.invalidateQueries({ queryKey: meetingsKeys.detail(data.id) });
    },
  });
}
